import React, {useState, useRef, useEffect} from 'react';
import {FaIcon} from '../../common/Icons.jsx';
import {Portal} from '../../common/Portal.jsx';
import {closeModal, Modal as ModalComponent} from '../../common/Modal.jsx';
import Select from '../../common/Select';
import DateInput from '../../common/DateInput';
import {DATA_TYPE_PROP_MAP} from '../../common/AgGrid';
import Errors from '../../common/Errors';
import {Button} from '../../bootstrap/index.jsx';
import {fetchJson, update} from '../../../helpers/index.js';
import {valueParser} from '../import_wizard/settings.js';

import {
  datagrid_create_bulk_action_path,
  datagrid_nbr_of_rows_bulk_action_path
} from '~/routes';

const MODAL_ID = 'bulk-actions-modal'

function BulkActionsModal({gridRef, handleCloseModal, datagrid, project_id}) {
  const [nbrOfRows, setNbrOfRows] = useState({row_count: 0});
  const [settings, setSettings] = useState({action_type: 'remove', mapping: []});
  const [saving, setSaving] = useState(false);

  const [errors, setErrors] = useState([]);
  const formRef = useRef();

  const handleClose = () => {
    closeModal(MODAL_ID);
    handleCloseModal();
  };

  const handleRadioChange = (field, val) => {
    updateSettings({[field]: val});
  };

  const handleColumnAdd = (e) => {
    e.preventDefault();
    updateSettings({mapping: [...settings.mapping, {}]});
  };

  const handleOptionMappingChange = (mapping_idx, option_idx, field, value) => {
    updateSettings({
      mapping:
        update(settings.mapping, {
          [mapping_idx]: {
            option_mappings: {
              [option_idx]: {$merge: { [field]: value }}
            }
          },
        })
    });
  };

  const handleMappingChange = (mapping_idx, field, value) => {
    let item = settings.mapping[mapping_idx];
    item[field] = value;
    item.new_value = ''

    let selectedCol = datagrid.columns.find(({accessor}) => accessor == value);
    if (selectedCol) {
      const {accessor, data_type, is_selection, selection_options = []} = selectedCol;

      item.accessor = accessor;
      item.data_type = data_type;
      item.is_selection = is_selection;
      item.selection_options = selection_options;
      item.option_mappings = selection_options.map(({ value }) => ({mapped_from: value, mapped_to: value}));
    }

    updateSettings({mapping: Object.values({...settings.mapping, [mapping_idx]: item})});
  };

  const handleMappingRemoval = (e, mapping_idx) => {
    e.preventDefault();
    settings.mapping.splice(mapping_idx, 1);
    updateSettings({mapping: [...settings.mapping]});
  };

  const handleMappingValueChange = (mapping_idx, field, value) => {
    let item = settings.mapping[mapping_idx];
    item[field] = value;

    updateSettings({mapping: Object.values({...settings.mapping, [mapping_idx]: item})});
  };

  const handleMappingValueChanged = (mapping_idx, field, new_value) => {
    let item = settings.mapping[mapping_idx];
    let current_value = item[field];

    if (current_value == new_value && new_value != '') {
      let parsed_value = valueParser(new_value, settings.mapping[mapping_idx]['data_type'], {});

      if (new_value != parsed_value) {
        item[field] = parsed_value;
        updateSettings({mapping: Object.values({...settings.mapping, [mapping_idx]: item})});
      }
    }
  };

  const updateSettings = (updatedSettings) => {
    setSettings((v) => ({...v, ...updatedSettings}));
  };

  useEffect(() => {
    fetchJson(datagrid_nbr_of_rows_bulk_action_path({datagrid_id: datagrid.id, project_id, _options: true}),{
      method: 'POST',
      body: JSON.stringify({ filter_model: gridRef.current.api.getFilterModel() })
    }).then(res => {
      setNbrOfRows(res);
    });
  }, [gridRef]);

  const handleSave = (e) => {
    e.preventDefault();

    fetchJson(datagrid_create_bulk_action_path({datagrid_id: datagrid.id, project_id, _options: true}),{
      method: 'POST',
      body: JSON.stringify({ bulk_action: {...settings, ...nbrOfRows, filter_model: gridRef.current.api.getFilterModel()} })
    }).then(res => {
      if (res.errors) {
        setErrors(res.errors);
      } else {
        setErrors([]);
        handleClose();
        window.ToastNotification({message: I18n.t(`datagrid_bulk_${settings.action_type}_started_info`), horizontal: 'center', vertical: 'top', autoHideDuration: 5});

      }
    });
  };

  return (
    <Portal
      id={MODAL_ID}
      size='md'
      title={I18n.t('bulk_actions')}
      saveLabel={I18n.t('save')}
      cancelLabel={I18n.t('close')}
      onClose={handleClose}
      onSave={handleSave}
      saving={saving}
      hideSaveButton={nbrOfRows.row_count == 0}
      component={ModalComponent}
    >
      <form ref={formRef} onSubmit={handleSave}>
        <Errors errors={errors}/>

        <div className='form-group'>
          <label>{I18n.t('nbr_rows_match_applied_filter')}: </label> {nbrOfRows.row_count}
        </div>

        <div className='form-check'>
          <input
              type='radio'
              className='form-check-label'
              checked={settings.action_type == 'remove'}
              onChange={(e) => handleRadioChange('action_type', 'remove')}
          />
          <label className='radio-inline-label'>{I18n.t('delete')} {'- Removes all filtered rows'}</label>
        </div>

        <div className='form-check'>
          <input
              type='radio'
              checked={settings.action_type == 'update'}
              onChange={(e) => handleRadioChange('action_type', 'update')}
          />
          <label className='radio-inline-label'>{I18n.t('update')}</label>
        </div>

        {settings.action_type == 'update' &&
          <>
            <hr />
            {settings.mapping.map((
                {
                  accessor = '',
                  data_type = '',
                  is_selection = false,
                  selection_options = [],
                  option_mappings = [],
                  new_value = ''
                }, idx) => (
                <div key={`col-${idx}`} style={{position: 'relative', padding: '10px', border: '1px solid #00000033', marginBottom: '10px'}}>
                  <div className='form-group'>
                    <label>{I18n.t('column')}</label>
                    <Select
                      options={datagrid.columns.map(({name, accessor}) => ({label: name, value: accessor}))}
                      value={accessor}
                      onChange={(val) => handleMappingChange(idx, 'accessor', val)}
                    />
                  </div>

                  { is_selection ?
                    <div className='form-group' style={{marginBottom: '3rem'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between'}}>
                        <label style={{marginBottom: 0}}>{I18n.t('mapping_values')}</label>
                      </div>
                      {
                        selection_options.map((mapped_from, i) => (
                          <div key={`mapping-${accessor}-${i}`} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem'}}>
                            <div style={{flex: 1, maxWidth: 'calc(50% - 15px)'}}>
                              <div className='form-control text-truncate bg-light'>
                                {mapped_from.label}
                              </div>
                              {i == selection_options.length - 1 && <span style={{position: 'absolute'}} className='description'>{I18n.t('from_value')}</span>}
                            </div>
                            <FaIcon type='arrow-right' />
                            <div style={{flex: 1, maxWidth: 'calc(50% - 15px)'}}>
                              <Select
                                options={selection_options}
                                value={option_mappings[i]['mapped_to'] || ''}//(idx, i, 'target', val)
                                onChange={(val) => handleOptionMappingChange(idx, i, 'mapped_to', val)}
                              />
                              {i == selection_options.length - 1 && <span style={{position: 'absolute'}} className='description'>{I18n.t('to_value')}</span>}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                    :
                    <div className='form-group'>
                      <label>{I18n.t('value')}</label>
                      {
                        ['date', 'timestamp'].includes(data_type) ?
                          <DateInput
                            defaultValue={new_value}
                            pickerOptions={{format: DATA_TYPE_PROP_MAP[data_type].cellEditorParams['format']}}
                            onChange={(val) => handleMappingValueChange(idx, 'new_value', val)}
                          />
                          :
                          <input
                            className='form-control'
                            value={new_value}
                            onChange={(e) => handleMappingValueChange(idx, 'new_value', e.target.value)}
                            onBlur={(_e) => handleMappingValueChanged(idx, 'new_value', new_value)}
                            onMouseLeave={(_e) => handleMappingValueChanged(idx, 'new_value', new_value)}
                          />
                      }
                    </div>
                }
                <a
                  href='#'
                  className='icon-trash btn-danger'
                  style={{position: 'absolute', top: 0, right: 0, padding: '5px'}}
                  title={I18n.t('delete')}
                  onClick={(e) => handleMappingRemoval(e, idx)}
                />
              </div>
            ))}
            <button className='btn btn-primary btn-sm mb-2' onClick={handleColumnAdd}><i className='icon-add'/> {I18n.t('add_column')}</button>
          </>
        }
      </form>
    </Portal>
  );
};

export default function BulkActionsButton({gridRef, datagrid, project_id}) {
  const [openModal, setOpenModal] = useState(false);

  const handleCloseModal = () => setOpenModal(false);
  const handleClick = () => setOpenModal(true);

  return (<>
    <a onClick={handleClick}>
      <FaIcon type='layer-group'/> {I18n.t('bulk_actions')}
    </a>

    {openModal && <BulkActionsModal {...{handleCloseModal, gridRef, datagrid, project_id}}/>}
  </>);
};
